import React, {useState} from 'react';
import {Spotlight, SpotlightData} from "types/Spotlight";
import {FormikHelpers} from "formik";
import Form, {FormBlock, FormSection, MediaField, TextField} from "components/Form/Form";
import {ToggleSwitch} from "components/Form/Form/Control";
import RepetitionBuilder from "../../../components/Form/RepetitionBuilder";
import {Frequency, RRule} from "rrule";
import {LinkUrlFieldSet, SiteAttachmentFieldSet} from "../../../components/Form/Form/Fieldset";
import {DateField} from "../../../components/Form/Form/Field";
import {fixRRuleUntil} from "../../../services/recurrence";
import * as Yup from 'yup';
import Label from "../../../components/Form/Label";
import {DateTime} from "luxon";
import {linkUrlRegex} from "../../../services/string";
import {useOperator} from "../../../context/OperatorContext";

// Types ----

type SpotlightFormProps = {
  spotlight?: Spotlight | null
  onSubmit: (values: SpotlightData, helpers: FormikHelpers<SpotlightData>) => any
}

// Component ----

function SpotlightForm({onSubmit, spotlight}: SpotlightFormProps) {
  const {termFor} = useOperator();
  const [isScheduled, setIsScheduled] = useState<boolean>(spotlight?.is_scheduled ?? false);
  const [isRecurring, setIsRecurring] = useState<boolean>(!!spotlight?.recurrence);

  function toggleScheduling(scheduled: boolean, {setFieldValue}: FormikHelpers<SpotlightData>) {
    if(scheduled) {
      // const enableAt = DateTime.now().plus({hours: 1});

      void setFieldValue('enable_at', spotlight?.enable_at?.toUTC().toISO() || '');
      void setFieldValue('disable_at', spotlight?.disable_at?.toUTC().toISO() || '');
    } else {
      void setFieldValue('enable_at', '');
      void setFieldValue('disable_at', '');
    }

    setIsScheduled(scheduled);

    if(!scheduled) {
      void setFieldValue('recurrence', null);
      setIsRecurring(false)
    }
  }

  function toggleRecurring(recurring: boolean, {setFieldValue}: FormikHelpers<SpotlightData>) {
    setIsRecurring(recurring);
    void setFieldValue('recurrence', recurring ? spotlight?.recurrence?.toString() || new RRule({freq: Frequency.DAILY, interval: 1}).toString() : null);
  }

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('This field is required').max(250, "Maximum character limit exceeded"),
    title: Yup.string().required('This field is required').max(30, "Maximum character limit exceeded"),
    text: Yup.string().max(50, "Maximum character limit exceeded"),
    url: Yup.string().required('This field is required').matches(linkUrlRegex, 'This field is required'),
    enable_at: isScheduled ? Yup.date().required('This field is required') : Yup.date(),
    disable_at:
      isScheduled ? Yup.date().when('enable_at', ([enableAt]: any, schema: any) => {
        if (!enableAt) return schema.required('This field is required');

        const minDate = DateTime.fromJSDate(enableAt).plus({minutes: 1}).toJSDate();
        return schema.min(minDate, "Must be after 'Enable at' date").required('This field is required');
      }) : Yup.date(),
  })

  const initialValues: SpotlightData = {
    name: spotlight?.name || '',
    title: spotlight?.title || '',
    text: spotlight?.text || '',
    url: spotlight?.url || '',
    offline_url: spotlight?.offline_url || '',
    image_id: spotlight?.image?.id || '',
    enable_at: spotlight?.enable_at?.toUTC().toISO() || '',
    disable_at: spotlight?.disable_at?.toUTC().toISO() || '',
    recurrence: spotlight?.recurrence?.toString(),
    // is_enabled: spotlight?.is_enabled ?? false,
    site_ids: spotlight?.sites?.map(site => site.id) || []
  }

  return (
    <Form initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      <FormSection
        name="Spotlight Content"
        description="The main content of the spotlight"
        columns={6}
      >

        {/* Name */}
        <FormBlock columns={4} extraLargeColumns={3}>
          <TextField
            name="name"
            label="Name *"
            hint="250 chars max"
            help="An internal name for this spotlight"
            autoComplete="off"
            maxLength={250}
          />
        </FormBlock>

        {/* Title */}
        <FormBlock columns={4}>
          <TextField
            name="title"
            label="Title *"
            hint="30 chars max"
            help="The title text shown to audience members"
            autoComplete="off"
            maxLength={30}
          />
        </FormBlock>

        {/* Text */}
        <FormBlock columns={4}>
          <TextField
            name="text"
            label="Text"
            hint="Optional. 50 chars max."
            help="Supplementary text shown below the title"
            autoComplete="off"
            maxLength={50}
          />
        </FormBlock>

        {/* Image */}
        <FormBlock columns={6}>
          <MediaField
            name="image_id"
            label="Image"
            hint="Optional"
            files={spotlight?.image}
            type={MediaField.types.IMAGE}
            maxFiles={1}
          />
        </FormBlock>

        {/* Link */}
        <FormBlock columns={6} extraLargeColumns={4}>
          <LinkUrlFieldSet label={'Link To *'} name={'url'} offlineName={'offline_url'}/>
        </FormBlock>
      </FormSection>

      <FormSection
        name={termFor('Sites')}
        description={`Choose which ${termFor('sites')} to associate this with.`}
        columns={6}
      >
        <FormBlock columns={6}>
          <SiteAttachmentFieldSet name={"site_ids"} selected={spotlight?.sites}/>
        </FormBlock>
      </FormSection>

      <FormSection
        name="Scheduling"
        description="Choose when to enable this spotlight"
        columns={6}
      >
        {/*/!* Enabled Toggle *!/*/}
        {/*<FormBlock columns={6}>*/}
        {/*  <ToggleField*/}
        {/*    name="is_enabled"*/}
        {/*    label="Enable"*/}
        {/*    description="Show this spotlight in the audience app"*/}
        {/*  />*/}
        {/*</FormBlock>*/}

        <FormBlock columns={6}>
          {form => (
            <ToggleSwitch
              label="Scheduled"
              description="Automatically enable/disable this spotlight"
              multiline
              checked={isScheduled}
              onChange={enabled => toggleScheduling(enabled, form)}
            />
          )}
        </FormBlock>

        {isScheduled &&
          <>
            <FormBlock columns={2}>
              <Label htmlFor="enable_at">Enable at *</Label>
              <div className='mt-2'>
                <DateField
                  type='datetime-local'
                  name='enable_at'
                  id='enable_at'
                />
              </div>
            </FormBlock>

            <FormBlock columns={2}>
              <Label htmlFor="disable_at">Disable at *</Label>
              <div className='mt-2'>
                <DateField
                  type='datetime-local'
                  name='disable_at'
                  id='disable_at'
                />
              </div>
            </FormBlock>

            <FormBlock columns={6}>
              {form => (
                <ToggleSwitch
                  label="Recurring"
                  description="Enable/disable this spotlight on a schedule. This uses the same times as selected above, unless recurring on an hourly basis"
                  multiline
                  checked={isRecurring}
                  onChange={enabled => toggleRecurring(enabled, form)}
                />
              )}
            </FormBlock>

            {isRecurring &&
              <FormBlock columns={6}>
                {form => (
                  <RepetitionBuilder
                    startDate={new Date()}
                    rule={form.values.recurrence ? RRule.fromString(form.values.recurrence) : null}
                    onChange={rule => {
                      const ruleString =  rule ? fixRRuleUntil(rule) : '';
                      void form.setFieldValue('recurrence', ruleString ?? null);
                    }}
                  />
                )}
              </FormBlock>
            }
          </>
        }
      </FormSection>
    </Form>
  )
}

// Export ----

export default SpotlightForm;